import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface ImageUploadProps {
  value?: File | null;
  onChange: (file: File | null) => void;
  label?: string;
  previewUrl?: string;
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  label = "Add Image (optional)",
  previewUrl,
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  // Simple effect - previewUrl takes priority for edit mode
  useEffect(() => {
    // Priority: previewUrl (for edit mode) > value (for new uploads)
    if (previewUrl) {
      setImagePreview(previewUrl);
    } else if (value) {
      // Only use FileReader for NEW uploads (when no previewUrl exists)
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setImagePreview(event.target.result as string);
        }
      };
      reader.readAsDataURL(value);
    } else {
      setImagePreview('');
    }
  }, [value, previewUrl]);

  const handleBrowseClick = () => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onChange(e.target.files[0]);
    }
  };

  const removeImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setImagePreview('');
    onChange(null);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {label}
      </Typography>

      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        accept="image/*"
        sx={{ display: 'none' }}
        onChange={handleImageChange}
        disabled={disabled}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={handleBrowseClick}
          disabled={disabled}
        >
          Browse...
        </Button>
        {imagePreview && (
          <Button
            color="error"
            onClick={removeImage}
            sx={{ ml: 2 }}
            disabled={disabled}
          >
            Remove Image
          </Button>
        )}
      </Box>

      {imagePreview && (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            maxWidth: '100%',
            maxHeight: '300px',
            overflow: 'hidden',
            borderRadius: 1,
            boxShadow: 1
          }}
        >
          <Box
            component="img"
            src={imagePreview}
            alt="Preview"
            sx={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ImageUpload;
